import { useEffect, useState } from "react";
import { certificatesAPI } from "../api";
import { Award } from "lucide-react";

const CertificatesSection = () => {
  const [certificates, setCertificates] = useState([]);

  useEffect(() => {
    certificatesAPI.getAll()
      .then((data) => setCertificates(data || []))
      .catch(() => setCertificates([]));
  }, []);

  if (!certificates.length) return null;

  const loop = [...certificates, ...certificates, ...certificates];

  return (
    <section className="py-16 bg-black border-y border-yellow-500/20" data-testid="certificates-section">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12 space-y-3">
          <span className="text-yellow-400 font-semibold text-sm uppercase tracking-wider inline-flex items-center gap-2">
            <Award size={16} /> Earned Certifications
          </span>
          <h2 className="text-3xl md:text-4xl font-bold text-white">
            My <span className="gradient-text">Credentials</span>
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto">
            Recognised certifications validating my skills and expertise
          </p>
        </div>

        <div className="relative overflow-hidden">
          <div className="absolute left-0 top-0 bottom-0 w-32 bg-gradient-to-r from-black to-transparent z-10 pointer-events-none"></div>
          <div className="absolute right-0 top-0 bottom-0 w-32 bg-gradient-to-l from-black to-transparent z-10 pointer-events-none"></div>

          <div
            className="flex items-stretch gap-8 md:gap-10 certificates-marquee"
            data-testid="certificates-marquee"
            style={{ width: "fit-content" }}
          >
            {loop.map((cert, index) => {
              const Tag = cert.link ? "a" : "div";
              const linkProps = cert.link
                ? { href: cert.link, target: "_blank", rel: "noopener noreferrer" }
                : {};
              return (
                <Tag
                  key={`${cert.id || cert.name}-${index}`}
                  {...linkProps}
                  className="flex-shrink-0 group relative block w-64 md:w-72"
                  data-testid="certificate-card"
                >
                  <div className="relative h-44 md:h-48 rounded-xl overflow-hidden bg-[#0d0d0d] border border-yellow-500/30 group-hover:border-yellow-400 transition-all duration-300 group-hover:shadow-[0_0_30px_rgba(230,194,0,0.5)]">
                    <img
                      src={cert.image}
                      alt={cert.name}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = "https://via.placeholder.com/400x250?text=" + encodeURIComponent(cert.name);
                      }}
                    />
                    {/* Gradient overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent opacity-90"></div>
                    {/* Year badge */}
                    {cert.year && (
                      <div className="absolute top-3 right-3 bg-yellow-400 text-black text-xs font-bold px-2 py-1 rounded">
                        {cert.year}
                      </div>
                    )}
                    {/* Bottom info */}
                    <div className="absolute bottom-0 left-0 right-0 p-4">
                      <h3 className="text-white font-bold text-sm md:text-base leading-tight line-clamp-2">
                        {cert.name}
                      </h3>
                      <p className="text-yellow-400 text-xs mt-1 font-medium">
                        {cert.issuer}
                      </p>
                    </div>
                  </div>
                </Tag>
              );
            })}
          </div>
        </div>

        <style jsx>{`
          .certificates-marquee {
            animation: scroll-certificates 35s linear infinite;
          }
          .certificates-marquee:hover {
            animation-play-state: paused;
          }
          @keyframes scroll-certificates {
            0%   { transform: translateX(0); }
            100% { transform: translateX(-33.333%); }
          }
        `}</style>
      </div>
    </section>
  );
};

export default CertificatesSection;
